import api from "../../services/api";
import { User, ProviderMembership } from "./types";

interface AuthResponse {
  success: boolean;
  message?: string;
  requireProfileCompletion?: boolean;
  data: {
    token: string;
    user: {
      id: string;
      name: string;
      email: string;
      phone?: string | null;
      roles: string[];
      providerMemberships: ProviderMembership[];
      profileCompleted?: boolean;
      authProvider?: 'email' | 'google' | 'apple' | 'facebook';
    };
    isNewUser?: boolean;
  };
}

interface MessageResponse {
  success: boolean;
  message: string;
}

interface SendOTPResponse {
  success: boolean;
  message: string;
  data?: {
    phone: string;
    isNewUser: boolean;
    expiresIn: number;
  };
  requiresName?: boolean;
}

interface VerifyOTPResponse {
  success: boolean;
  message: string;
  requiresName?: boolean;
  data?: {
    token: string;
    user: {
      id: string;
      name: string;
      email: string;
      phone: string;
      roles: string[];
      providerMemberships: ProviderMembership[];
    };
    isNewUser?: boolean;
  };
}

export const loginUserAPI = async (
  data: { email: string; password: string }
): Promise<User> => {
  const response = await api.post<AuthResponse>("/auth/login", data);
  // Transform backend response to match frontend User type
  return {
    ...response.data.data.user,
    token: response.data.data.token,
  };
};

export const registerUserAPI = async (
  data: { name: string; email: string; phone: string; password: string }
): Promise<User> => {
  const response = await api.post<AuthResponse>("/auth/register", data);
  // Transform backend response to match frontend User type
  return {
    ...response.data.data.user,
    token: response.data.data.token,
  };
};

export const sendEmailCodeAPI = async (
  data: { name: string; email: string }
): Promise<{ email: string; expiresIn: number }> => {
  const response = await api.post<{ success: boolean; message: string; data: { email: string; expiresIn: number } }>(
    "/auth/send-email-code",
    data
  );
  return response.data.data;
};

export const verifyEmailCodeAPI = async (
  data: { email: string; code: string }
): Promise<void> => {
  await api.post("/auth/verify-email-code", data);
};

export const googleAuthAPI = async (credential: string): Promise<User> => {
  const response = await api.post<AuthResponse>("/auth/google", { credential });
  // Transform backend response to match frontend User type
  // Also surface requireProfileCompletion so callers can redirect
  return {
    ...response.data.data.user,
    token: response.data.data.token,
    profileCompleted: response.data.data.user.profileCompleted ?? !response.data.requireProfileCompletion,
  };
};

export const forgotPasswordAPI = async (email: string): Promise<string> => {
  const response = await api.post<MessageResponse>("/auth/forgot-password", { email });
  return response.data.message;
};

export const resetPasswordAPI = async (
  token: string,
  password: string
): Promise<User> => {
  const response = await api.post<AuthResponse>(`/auth/reset-password/${token}`, { password });
  return {
    ...response.data.data.user,
    token: response.data.data.token,
  };
};

// OTP Login APIs
export const sendOTPAPI = async (phone: string): Promise<SendOTPResponse> => {
  const response = await api.post<SendOTPResponse>("/auth/send-otp", { phone });
  return response.data;
};

export const verifyOTPAPI = async (
  phone: string,
  otp: string,
  name?: string
): Promise<{ user: User; isNewUser: boolean } | { requiresName: true }> => {
  const response = await api.post<VerifyOTPResponse>("/auth/verify-otp", { phone, otp, name });
  
  if (response.data.requiresName) {
    return { requiresName: true };
  }
  
  if (!response.data.data) {
    throw new Error(response.data.message || "Verification failed");
  }
  
  return {
    user: {
      ...response.data.data.user,
      token: response.data.data.token,
    },
    isNewUser: response.data.data.isNewUser || false,
  };
};

export const resendOTPAPI = async (phone: string): Promise<SendOTPResponse> => {
  const response = await api.post<SendOTPResponse>("/auth/resend-otp", { phone });
  return response.data;
};

// Guest OTP verification - only verifies phone, doesn't create user
interface VerifyGuestOTPResponse {
  success: boolean;
  message: string;
  data?: {
    phone: string;
    verified: boolean;
    hasExistingAccount: boolean;
  };
}

export const verifyGuestOTPAPI = async (
  phone: string,
  otp: string
): Promise<{ verified: boolean; hasExistingAccount: boolean }> => {
  const response = await api.post<VerifyGuestOTPResponse>("/auth/verify-guest-otp", { phone, otp });
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || "Verification failed");
  }
  
  return {
    verified: response.data.data.verified,
    hasExistingAccount: response.data.data.hasExistingAccount,
  };
};

// Avatar Update API
interface AvatarUpdateResponse {
  success: boolean;
  message: string;
  data: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    roles: string[];
    providerMemberships: ProviderMembership[];
    profile?: {
      avatar?: string;
      gender?: string;
      dateOfBirth?: string;
      address?: {
        street?: string;
        city?: string;
        state?: string;
        country?: string;
        postalCode?: string;
      };
    };
  };
}

export const updateAvatarAPI = async (avatar: string): Promise<User> => {
  const token = localStorage.getItem("token");
  const response = await api.put<AvatarUpdateResponse>(
    "/users/me/avatar",
    { avatar },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  // Transform backend response - user data is directly in data, not data.user
  // Also _id needs to be mapped to id
  const userData = response.data.data;
  return {
    id: userData._id,
    name: userData.name,
    email: userData.email,
    phone: userData.phone,
    roles: userData.roles,
    providerMemberships: userData.providerMemberships,
    profile: userData.profile,
    token: token || "",
  };
};

// Complete Profile API — for Google-OAuth users who need to add their phone number
interface CompleteProfileResponse {
  success: boolean;
  message: string;
  data: {
    user: {
      id: string;
      name: string;
      email: string;
      phone: string;
      profileCompleted: boolean;
      roles: string[];
      providerMemberships: ProviderMembership[];
    };
  };
}

export const completeProfileAPI = async (phone: string): Promise<User> => {
  const token = localStorage.getItem("token") || "";
  const response = await api.post<CompleteProfileResponse>("/auth/complete-profile", { phone });
  const userData = response.data.data.user;
  return {
    id: userData.id,
    name: userData.name,
    email: userData.email,
    phone: userData.phone,
    profileCompleted: true,
    roles: userData.roles,
    providerMemberships: userData.providerMemberships,
    token,
  };
};
